import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ── Types ──────────────────────────────────────────────────────────

export interface TargetUniverseAccount {
  id: string;
  name: string;
  domain: string | null;
  logo_url: string | null;
  website: string | null;
  primary_category: string;
  ai_subcategory: string | null;
  pipeline_stage: string | null;
  engagement_priority: string | null;
  warm_intro_available: boolean | null;
  champion_identified: boolean | null;
  human_summary: string | null;
  targeting_assigned_to: string | null;
  qualification_reason: string | null;
  next_followup_date: string | null;
  last_reviewed_at: string | null;
  headquarters_city: string | null;
  headquarters_state: string | null;
  headquarters_country: string | null;
  employee_range: string | null;
  development_stage: string | null;
  description: string | null;
  icp_score: number | null;
  icp_tier: string | null;
  // CARR-specific
  sales_rep: string | null;
  territory: string | null;
  carr_modality: string | null;
  modalities: string[] | null;
  clinical_phase: string | null;
  is_icp_relevant: boolean | null;
  // Account Operating Model (account_tier = Account Status)
  account_tier: string | null;
  buying_mode: string | null;
  budget_confirmed: boolean;
  fit_confirmed: boolean;
  tier_changed_at: string | null;
  tier_changed_by: string | null;
  pipeline_gate_met: boolean;
  needs_classification: boolean;
  contact_count: number;
  contacts_with_email: number;
  followup_group_count: number;
  followup_groups: string | null;
  has_description: boolean;
  has_classification: boolean;
  has_contacts: boolean;
  last_activity_at: string | null;
}

export interface TargetUniverseStats {
  total_accounts: number;
  not_yet_reached: number;
  queued: number;
  targeted: number;
  opportunity: number;
  nurture: number;
  disqualified: number;
  parked: number;
  // Account status counts
  tier_unreviewed: number;
  tier_cold: number;
  tier_validated_target: number;
  tier_nurture: number;
  tier_pipeline_ready: number;
  tier_customer: number;
  tier_disqualified: number;
  tier_parked: number;
  pipeline_gate_met: number;
  active_opps: number;
  // Operational
  followups_this_week: number;
  overdue_followups: number;
  high_priority: number;
  reviewed: number;
}

export interface AccountActivity {
  id: string;
  account_id: string;
  activity_type: string;
  performed_by: string | null;
  description: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

export interface FollowupGroup {
  id: string;
  name: string;
  description: string | null;
  cadence: string;
  color: string;
  is_active: boolean;
}

export type PipelineStage =
  | "not_yet_reached"
  | "queued"
  | "targeted"
  | "opportunity"
  | "nurture_monthly"
  | "nurture_3month"
  | "nurture_6month"
  | "disqualified"
  | "parked";

export type AccountTier =
  | "unreviewed"
  | "cold"
  | "validated_target"
  | "nurture"
  | "pipeline_ready"
  | "customer"
  | "disqualified"
  | "parked";

export type BuyingMode = "growth" | "trouble" | "even_keel" | "overconfident" | "unknown";

export interface TargetUniverseFilters {
  search: string;
  category: string[];
  subcategory: string[];
  hasContacts: boolean | null;
  assignedTo: string[];
  country: string[];
  // CARR-specific
  territory: string[];
  rep: string[];
  carrModality: string[];
  modality: string[];
  therapeuticArea: string[];
  clinicalPhase: string[];
  fundingStatus: string[];
  employeeRange: string[];
  // Account Operating Model
  accountTier: string[];
  icpTier: string[];
  topAccount: boolean;
}

const ROWS_PER_PAGE = 50;

// ── Stats Hook ─────────────────────────────────────────────────────

export function useTargetUniverseStats() {
  return useQuery({
    queryKey: ["target-universe-stats"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_target_universe_stats")
        .select("*")
        .single();
      if (error) throw error;
      return data as TargetUniverseStats;
    },
  });
}

// ── List Hook ──────────────────────────────────────────────────────

export function useTargetUniverseList(
  filters: TargetUniverseFilters,
  page: number
) {
  return useQuery({
    queryKey: ["target-universe-list", filters, page],
    queryFn: async () => {
      const offset = page * ROWS_PER_PAGE;

      let query = (supabase as any)
        .from("v_target_universe")
        .select("*", { count: "exact" });

      if (filters.search) {
        query = query.ilike("name", `%${filters.search}%`);
      }

      if (filters.category.length > 0) {
        query = query.in("primary_category", filters.category);
      }

      if (filters.hasContacts === true) {
        query = query.gt("contact_count", 0);
      } else if (filters.hasContacts === false) {
        query = query.eq("contact_count", 0);
      }

      if (filters.assignedTo.length > 0) {
        query = query.in("targeting_assigned_to", filters.assignedTo);
      }

      if (filters.country.length > 0) {
        query = query.in("headquarters_country", filters.country);
      }

      // CARR-specific filters
      if (filters.territory.length > 0) {
        query = query.in("territory", filters.territory);
      }

      if (filters.rep.length > 0) {
        query = query.in("sales_rep", filters.rep);
      }

      if (filters.carrModality.length > 0) {
        query = query.in("carr_modality", filters.carrModality);
      }

      if (filters.modality.length > 0) {
        query = query.overlaps("modalities", filters.modality);
      }

      if (filters.clinicalPhase.length > 0) {
        query = query.in("clinical_phase", filters.clinicalPhase);
      }

      if (filters.subcategory.length > 0) {
        query = query.in("ai_subcategory", filters.subcategory);
      }

      if (filters.therapeuticArea.length > 0) {
        query = query.overlaps("therapeutic_areas", filters.therapeuticArea);
      }

      if (filters.fundingStatus.length > 0) {
        query = query.in("funding_status", filters.fundingStatus);
      }

      if (filters.employeeRange.length > 0) {
        query = query.in("employee_range", filters.employeeRange);
      }

      // Account Operating Model filters
      if (filters.accountTier.length > 0) {
        query = query.in("account_tier", filters.accountTier);
      }

      if (filters.icpTier.length > 0) {
        query = query.in("icp_tier", filters.icpTier);
      }

      if (filters.topAccount) {
        query = query.eq("top_account", true);
      }

      query = query.order("name").range(offset, offset + ROWS_PER_PAGE - 1);

      const { data, error, count } = await query;
      if (error) throw error;
      return { data: (data || []) as TargetUniverseAccount[], count: count || 0 };
    },
  });
}

// ── Account Update Mutation ────────────────────────────────────────

interface UpdateAccountFields {
  targeting_status?: string | null;
  engagement_priority?: string | null;
  warm_intro_available?: boolean;
  champion_identified?: boolean;
  targeting_notes?: string | null;
  targeting_assigned_to?: string | null;
  qualification_reason?: string | null;
  next_followup_date?: string | null;
  last_reviewed_at?: string;
  last_reviewed_by?: string;
  // Account Operating Model
  account_tier?: string | null;
  buying_mode?: string | null;
  budget_confirmed?: boolean;
  fit_confirmed?: boolean;
  tier_changed_at?: string;
  tier_changed_by?: string;
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      fields,
      activityType,
      activityDescription,
      activityMetadata,
    }: {
      accountId: string;
      fields: UpdateAccountFields;
      activityType?: string;
      activityDescription?: string;
      activityMetadata?: Record<string, any>;
      performedBy?: { id: string | null; name: string | null };
    }) => {
      const { error: updateError } = await (supabase as any)
        .from("companies")
        .update(fields)
        .eq("id", accountId);
      if (updateError) throw updateError;

      if (activityType) {
        const { error: activityError } = await (supabase as any)
          .from("account_activities")
          .insert({
            account_id: accountId,
            activity_type: activityType,
            performed_by: performedBy?.name || "User",
            performed_by_id: performedBy?.id || null,
            description: activityDescription || null,
            metadata: activityMetadata || {},
          });
        if (activityError) throw activityError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["target-universe-list"] });
      queryClient.invalidateQueries({ queryKey: ["target-universe-stats"] });
      queryClient.invalidateQueries({ queryKey: ["account-activities"] });
      queryClient.invalidateQueries({ queryKey: ["company-detail"] });
    },
  });
}

// ── Bulk Update Mutation ───────────────────────────────────────────

export function useBulkUpdateAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountIds,
      fields,
      activityType,
      activityDescription,
    }: {
      accountIds: string[];
      fields: UpdateAccountFields;
      activityType?: string;
      activityDescription?: string;
      performedBy?: { id: string | null; name: string | null };
    }) => {
      const { error: updateError } = await (supabase as any)
        .from("companies")
        .update(fields)
        .in("id", accountIds);
      if (updateError) throw updateError;

      if (activityType) {
        const activities = accountIds.map((id) => ({
          account_id: id,
          activity_type: activityType,
          performed_by: performedBy?.name || "User",
          performed_by_id: performedBy?.id || null,
          description: activityDescription || null,
          metadata: {},
        }));
        const { error: activityError } = await (supabase as any)
          .from("account_activities")
          .insert(activities);
        if (activityError) throw activityError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["target-universe-list"] });
      queryClient.invalidateQueries({ queryKey: ["target-universe-stats"] });
    },
  });
}

// ── Activities Hook ────────────────────────────────────────────────

export function useAccountActivities(accountId: string | null) {
  return useQuery({
    queryKey: ["account-activities", accountId],
    enabled: !!accountId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("account_activities")
        .select("*")
        .eq("account_id", accountId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data || []) as AccountActivity[];
    },
  });
}

// ── Follow-up Groups Hook ──────────────────────────────────────────

export function useFollowupGroups() {
  return useQuery({
    queryKey: ["followup-groups"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("followup_groups")
        .select("*")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return (data || []) as FollowupGroup[];
    },
  });
}

// ── Account Follow-up Group Management ─────────────────────────────

export function useAccountFollowupGroups(accountId: string | null) {
  return useQuery({
    queryKey: ["account-followup-groups", accountId],
    enabled: !!accountId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("account_followup_groups")
        .select("group_id, added_at, notes, followup_groups(id, name, cadence, color)")
        .eq("account_id", accountId);
      if (error) throw error;
      return data || [];
    },
  });
}

const CADENCE_DAYS: Record<string, number> = {
  monthly: 30,
  quarterly: 90,
  bi_annual: 180,
};

export function useToggleAccountFollowupGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      groupId,
      action,
      cadence,
    }: {
      accountId: string;
      groupId: string;
      action: "add" | "remove";
      cadence?: string;
    }) => {
      if (action === "add") {
        const { error } = await (supabase as any)
          .from("account_followup_groups")
          .insert({ account_id: accountId, group_id: groupId });
        if (error) throw error;

        // Auto-schedule next follow-up date based on cadence
        if (cadence && CADENCE_DAYS[cadence]) {
          const nextDate = new Date();
          nextDate.setDate(nextDate.getDate() + CADENCE_DAYS[cadence]);
          const dateStr = nextDate.toISOString().split("T")[0];
          await (supabase as any)
            .from("companies")
            .update({ next_followup_date: dateStr })
            .eq("id", accountId)
            .is("next_followup_date", null); // Only set if not already scheduled
        }
      } else {
        const { error } = await (supabase as any)
          .from("account_followup_groups")
          .delete()
          .eq("account_id", accountId)
          .eq("group_id", groupId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-followup-groups"] });
      queryClient.invalidateQueries({ queryKey: ["target-universe-list"] });
      queryClient.invalidateQueries({ queryKey: ["target-universe-kanban"] });
    },
  });
}

export { CADENCE_DAYS };

// ── Contacts for Review Panel ──────────────────────────────────────

export function useAccountTopContacts(accountId: string | null) {
  return useQuery({
    queryKey: ["account-top-contacts", accountId],
    enabled: !!accountId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("contacts")
        .select("id, first_name, last_name, job_title, email, linkedin_url, seniority_level")
        .eq("company_id", accountId)
        .order("seniority_level", { ascending: true })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
  });
}

// ── Filter Options Hook ────────────────────────────────────────────

export function useTargetUniverseFilterOptions() {
  return useQuery({
    queryKey: ["target-universe-filter-options"],
    staleTime: 1000 * 60 * 5, // 5 min — facets don't change often
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc(
        "get_target_universe_filter_facets"
      );
      if (error) throw error;

      const empty: { value: string; label: string; count: number }[] = [];
      return {
        categories: data?.categories || empty,
        subcategories: data?.subcategories || empty,
        countries: data?.countries || empty,
        assignedTo: data?.assignedTo || empty,
        territories: data?.territories || empty,
        reps: data?.reps || empty,
        carrModalities: data?.carrModalities || empty,
        modalities: data?.modalities || empty,
        therapeuticAreas: data?.therapeuticAreas || empty,
        clinicalPhases: data?.clinicalPhases || empty,
        fundingStatuses: data?.fundingStatuses || empty,
        employeeRanges: data?.employeeRanges || empty,
        accountTiers: data?.accountTiers || empty,
        buyingModes: data?.buyingModes || empty,
        icpTiers: data?.icpTiers || empty,
      };
    },
  });
}

// ── Team Reps Hook (replaces hardcoded CARR_REPS) ─────────────────

export interface TeamRep {
  id: string;
  first_name: string;
  full_name: string;
}

export function useTeamReps() {
  return useQuery({
    queryKey: ["team-reps"],
    staleTime: 1000 * 60 * 30, // 30 min — rarely changes
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("team_profiles")
        .select("id, first_name, full_name")
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return (data || []) as TeamRep[];
    },
  });
}

export { ROWS_PER_PAGE };

// ── Engagement History Hook (for Review Panel) ────────────────────

export interface EngagementEvent {
  id: string;
  type: "outreach" | "campaign_enrollment";
  title: string;
  detail: string | null;
  date: string;
  status: string | null;
}

export function useAccountEngagementHistory(accountId: string | null) {
  return useQuery({
    queryKey: ["account-engagement-history", accountId],
    enabled: !!accountId,
    queryFn: async () => {
      const events: EngagementEvent[] = [];

      // Outreach messages sent to this account
      const { data: outreach } = await (supabase as any)
        .from("outreach_messages")
        .select("id, message_type, sender_name, status, sent_at, created_at, contact_name")
        .eq("account_id", accountId)
        .order("created_at", { ascending: false })
        .limit(20);

      if (outreach) {
        for (const m of outreach) {
          events.push({
            id: m.id,
            type: "outreach",
            title: `${m.message_type?.replace(/_/g, " ") || "Message"} → ${m.contact_name || "contact"}`,
            detail: m.sender_name ? `by ${m.sender_name}` : null,
            date: m.sent_at || m.created_at,
            status: m.status,
          });
        }
      }

      // Campaign enrollments via contacts
      const { data: enrollments } = await (supabase as any)
        .from("campaign_contacts")
        .select("id, engagement_status, enrolled_at, sent_at, replied_at, contacts!inner(company_id, first_name, last_name), campaigns!inner(name)")
        .eq("contacts.company_id", accountId)
        .order("enrolled_at", { ascending: false })
        .limit(20);

      if (enrollments) {
        for (const e of enrollments) {
          events.push({
            id: e.id,
            type: "campaign_enrollment",
            title: `${e.contacts?.first_name || ""} ${e.contacts?.last_name || ""} → ${e.campaigns?.name || "Campaign"}`,
            detail: e.engagement_status,
            date: e.enrolled_at || e.sent_at || e.replied_at,
            status: e.engagement_status,
          });
        }
      }

      // Sort by date descending
      events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      return events.slice(0, 20);
    },
  });
}

// ── Kanban Per-Tier Data Hook ──────────────────────────────────────

const KANBAN_TIERS = [
  "unreviewed",
  "cold",
  "validated_target",
  "nurture",
  "pipeline_ready",
  "customer",
  "disqualified",
  "parked",
] as const;

const DEFAULT_KANBAN_LIMIT = 50;

export interface KanbanTierData {
  accounts: TargetUniverseAccount[];
  totalCount: number;
  hasMore: boolean;
}

export type KanbanData = Record<string, KanbanTierData>;

function applyFiltersToQuery(query: any, filters: TargetUniverseFilters) {
  if (filters.search) query = query.ilike("name", `%${filters.search}%`);
  if (filters.category.length > 0) query = query.in("primary_category", filters.category);
  if (filters.hasContacts === true) query = query.gt("contact_count", 0);
  else if (filters.hasContacts === false) query = query.eq("contact_count", 0);
  if (filters.assignedTo.length > 0) query = query.in("targeting_assigned_to", filters.assignedTo);
  if (filters.country.length > 0) query = query.in("headquarters_country", filters.country);
  if (filters.territory.length > 0) query = query.in("territory", filters.territory);
  if (filters.rep.length > 0) query = query.in("sales_rep", filters.rep);
  if (filters.carrModality.length > 0) query = query.in("carr_modality", filters.carrModality);
  if (filters.modality.length > 0) query = query.overlaps("modalities", filters.modality);
  if (filters.clinicalPhase.length > 0) query = query.in("clinical_phase", filters.clinicalPhase);
  if (filters.subcategory.length > 0) query = query.in("ai_subcategory", filters.subcategory);
  if (filters.therapeuticArea.length > 0) query = query.overlaps("therapeutic_areas", filters.therapeuticArea);
  if (filters.fundingStatus.length > 0) query = query.in("funding_status", filters.fundingStatus);
  if (filters.employeeRange.length > 0) query = query.in("employee_range", filters.employeeRange);
  if (filters.accountTier.length > 0) query = query.in("account_tier", filters.accountTier);
  if (filters.icpTier.length > 0) query = query.in("icp_tier", filters.icpTier);
  if (filters.topAccount) query = query.eq("top_account", true);
  return query;
}

export function useTargetUniverseKanbanData(
  filters: TargetUniverseFilters,
  tierLimits: Record<string, number> = {}
) {
  return useQuery({
    queryKey: ["target-universe-kanban", filters, tierLimits],
    queryFn: async () => {
      const results = await Promise.all(
        KANBAN_TIERS.map(async (tier) => {
          const limit = tierLimits[tier] || DEFAULT_KANBAN_LIMIT;
          let query = (supabase as any)
            .from("v_target_universe")
            .select("*", { count: "exact" })
            .eq("account_tier", tier);

          query = applyFiltersToQuery(query, filters);
          query = query.order("name").limit(limit);

          const { data, error, count } = await query;
          if (error) throw error;

          return {
            tier,
            accounts: (data || []) as TargetUniverseAccount[],
            totalCount: count || 0,
            hasMore: (count || 0) > limit,
          };
        })
      );

      return results.reduce<KanbanData>((acc, r) => {
        acc[r.tier] = {
          accounts: r.accounts,
          totalCount: r.totalCount,
          hasMore: r.hasMore,
        };
        return acc;
      }, {});
    },
  });
}
